import React, { Component } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import {
  searchPools,
  searchSponsors,
  addPool,
  editPoolForSponsor,
} from '../../services/api/pool-manager';
import SearchSelect from './SearchSelect';
import { RegularText, BoldText } from '../common/Text';

const Card = styled.div`
  display: flex;
  flex-direction: column;
  margin: 12px;
  padding: 22px;
  background: #ffffff;
  border: 1px solid #eceaf3;
  border-radius: 14px;
  box-shadow: 0 6px 20px rgba(75, 46, 131, 0.06);
`;

const SectionTitle = styled(BoldText)`
  margin-bottom: 4px;
`;

const Divider = styled.div`
  height: 1px;
  background: #f1eff7;
  margin: 22px 0;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 18px;
  margin-top: 12px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled(RegularText)`
  color: #7a7391;
  font-size: 0.8rem;
  margin-bottom: 6px;
`;

const Input = styled.input`
  font-family: BrandonText-Medium, Lato, sans-serif;
  padding: 11px 14px;
  border: 1px solid #ddd6ee;
  border-radius: 10px;
  font-size: 0.9rem;
  min-width: 18rem;
  outline: none;
  :focus {
    border-color: #6b4bd6;
  }
`;

const Button = styled.button`
  font-family: BrandonText-Medium, Lato, sans-serif;
  font-size: 0.9rem;
  padding: 11px 22px;
  border: none;
  border-radius: 10px;
  color: white;
  background: linear-gradient(135deg, #6b4bd6, #4b2e83);
  box-shadow: 0 6px 14px rgba(75, 46, 131, 0.22);
  cursor: pointer;
  transition: opacity 0.15s ease;
  :hover {
    opacity: 0.9;
  }
  :disabled {
    background: #c5c0d4;
    box-shadow: none;
    cursor: not-allowed;
  }
`;

const HelperText = styled(RegularText)`
  color: #8a84a0;
  margin-top: 8px;
`;

class ManagePoolsPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // create
      newPoolName: '',
      copyFromPoolId: '',
      copyFromOption: null,
      creating: false,
      // assign
      assignSponsorId: '',
      assignSponsorOption: null,
      assignPoolId: '',
      assignPoolOption: null,
      assigning: false,
    };
  }

  // Backend pool search (server-side, capped at 50). Shared by copy-from + assign pool pickers.
  fetchPoolOptions = (input) => {
    const term = (input || '').trim();
    return searchPools(term)
      .then((res) =>
        res && res.message === 'success'
          ? (res.pools || []).map((p) => ({
              value: p.poolId,
              label: `${p.poolName} (${p.poolId})`,
            }))
          : []
      )
      .catch(() => []);
  };

  // Backend sponsor search (server-side, capped at 50).
  fetchSponsorOptions = (input) => {
    const term = (input || '').trim();
    return searchSponsors(term)
      .then((res) =>
        res && res.message === 'success'
          ? (res.sponsors || []).map((s) => ({
              value: s.sponsorId,
              label: `${s.sponsorName} (${s.sponsorId})`,
            }))
          : []
      )
      .catch(() => []);
  };

  onCreatePool = () => {
    const { newPoolName, copyFromPoolId } = this.state;
    const name = newPoolName.trim();
    if (!name) {
      toast.error('Enter a pool name.');
      return;
    }
    this.setState({ creating: true });
    addPool(name, copyFromPoolId || undefined)
      .then((res) => {
        if (res && res.message === 'success') {
          toast.success(`Pool "${name}" created.`);
          this.setState({
            newPoolName: '',
            copyFromPoolId: '',
            copyFromOption: null,
          });
          if (this.props.onChanged) {
            this.props.onChanged();
          }
        } else {
          throw new Error((res && res.errorMessage) || 'Failed to create pool');
        }
      })
      .catch((err) => {
        toast.error(err.message || 'Failed to create pool');
      })
      .finally(() => {
        this.setState({ creating: false });
      });
  };

  onAssignPool = () => {
    const { assignSponsorId, assignPoolId } = this.state;
    if (!assignSponsorId || !assignPoolId) {
      toast.error('Select both a sponsor and a pool.');
      return;
    }
    this.setState({ assigning: true });
    editPoolForSponsor(assignPoolId, assignSponsorId)
      .then((res) => {
        if (res && res.message === 'success') {
          toast.success('Sponsor pool updated.');
          this.setState({
            assignSponsorId: '',
            assignSponsorOption: null,
            assignPoolId: '',
            assignPoolOption: null,
          });
          if (this.props.onChanged) {
            this.props.onChanged();
          }
        } else {
          throw new Error(
            (res && res.errorMessage) || 'Failed to update sponsor pool'
          );
        }
      })
      .catch((err) => {
        toast.error(err.message || 'Failed to update sponsor pool');
      })
      .finally(() => {
        this.setState({ assigning: false });
      });
  };

  render() {
    const {
      newPoolName,
      copyFromOption,
      creating,
      assignSponsorOption,
      assignPoolOption,
      assigning,
    } = this.state;

    return (
      <Card>
        <BoldText fontSize="1.2rem">Manage Pools</BoldText>
        <HelperText>Create a pool, or assign a pool to a sponsor.</HelperText>

        <React.Fragment>
            <Divider />
            <SectionTitle>Create a new pool</SectionTitle>
            <Row>
              <Field>
                <Label>Pool name</Label>
                <Input
                  value={newPoolName}
                  placeholder="e.g. Acme Corp Pool"
                  onChange={(e) =>
                    this.setState({ newPoolName: e.target.value })
                  }
                />
              </Field>
              <Field>
                <Label>Copy doctors from (optional)</Label>
                <SearchSelect
                  placeholder="Search pool…"
                  loadOptions={this.fetchPoolOptions}
                  selected={copyFromOption}
                  onSelect={(option) =>
                    this.setState({
                      copyFromOption: option || null,
                      copyFromPoolId: option ? option.value : '',
                    })
                  }
                />
              </Field>
              <Button disabled={creating} onClick={this.onCreatePool}>
                {creating ? 'Creating…' : 'Create pool'}
              </Button>
            </Row>

            <Divider />
            <SectionTitle>Assign pool to sponsor</SectionTitle>
            <Row>
              <Field>
                <Label>Sponsor</Label>
                <SearchSelect
                  placeholder="Search sponsor…"
                  loadOptions={this.fetchSponsorOptions}
                  selected={assignSponsorOption}
                  onSelect={(option) =>
                    this.setState({
                      assignSponsorOption: option || null,
                      assignSponsorId: option ? option.value : '',
                    })
                  }
                />
              </Field>
              <Field>
                <Label>Pool</Label>
                <SearchSelect
                  placeholder="Search pool…"
                  loadOptions={this.fetchPoolOptions}
                  selected={assignPoolOption}
                  onSelect={(option) =>
                    this.setState({
                      assignPoolOption: option || null,
                      assignPoolId: option ? option.value : '',
                    })
                  }
                />
              </Field>
              <Button disabled={assigning} onClick={this.onAssignPool}>
                {assigning ? 'Updating…' : 'Update sponsor pool'}
              </Button>
            </Row>
          </React.Fragment>
      </Card>
    );
  }
}

export default ManagePoolsPanel;
